import React, { useEffect, useState } from 'react';
import { getPassportOwnerAnalytics } from '../../services/api/passport';
import { verificationLevelLabel } from './passportCopy';
import apiService from '../../services/ApiService';

type Props = {
  vehicleId: number;
  isOwner: boolean;
};

/**
 * Owner-only Passport analytics strip for a vehicle page.
 * Never shows scanner identities — aggregates and verification mix only.
 */
const VehiclePassportOwnerPanel: React.FC<Props> = ({ vehicleId, isOwner }) => {
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOwner || !vehicleId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getPassportOwnerAnalytics(vehicleId);
        if (!cancelled) {
          setData(res);
          void apiService.trackEvent('passport_owner_analytics_viewed', {
            metadata: { vehicle_id: vehicleId },
          });
        }
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unavailable');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vehicleId, isOwner]);

  if (!isOwner) return null;
  if (error) {
    return (
      <div className="mt-4 rounded-xl border border-white/10 px-4 py-3 text-sm text-zinc-400" role="status">
        Passport owner analytics unavailable.
      </div>
    );
  }
  if (!data) return null;

  const agg = data.aggregates || {};
  const mix = Array.isArray(data.verification_mix) ? data.verification_mix : [];
  const daily = Array.isArray(data.daily_30d) ? data.daily_30d : [];
  const maxDaily = Math.max(1, ...daily.map((d: any) => Number(d.unique_users || 0)));

  return (
    <section
      className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5"
      aria-labelledby="passport-owner-analytics"
    >
      <h2 id="passport-owner-analytics" className="font-display text-lg text-white">
        Passport — seen in person
      </h2>
      <p className="mt-1 text-sm text-zinc-400">
        Unique collectors only. Pending, rejected, and your own scans are excluded from public-facing totals.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-500">Basic QR</p>
          <p className="text-2xl text-white">{agg.unique_basic_count ?? 0}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-500">Stronger verification</p>
          <p className="text-2xl text-white">{agg.unique_strong_count ?? 0}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-500">Held / flagged</p>
          <p className="text-2xl text-white">{data.suspicious_or_held ?? 0}</p>
        </div>
      </div>

      {mix.length > 0 ? (
        <ul className="mt-4 space-y-1 text-sm text-zinc-300">
          {mix.map((m: any) => (
            <li key={m.verification_level}>
              {verificationLevelLabel(m.verification_level)} — {m.unique_users} people ({m.total} scans)
            </li>
          ))}
        </ul>
      ) : null}

      {daily.length > 0 ? (
        <div className="mt-5" aria-label="Unique collectors over the last 30 days">
          <p className="text-xs uppercase tracking-wide text-zinc-500">Last 30 days</p>
          <div className="mt-2 flex h-16 items-end gap-0.5" role="img" aria-label="Daily unique collectors chart">
            {daily.map((d: any) => (
              <div
                key={d.date}
                title={`${d.date}: ${d.unique_users}`}
                className="flex-1 rounded-sm bg-orange-500/70"
                style={{ height: `${Math.max(8, (Number(d.unique_users) / maxDaily) * 100)}%` }}
              />
            ))}
          </div>
        </div>
      ) : null}

      <p className="mt-4 text-xs text-zinc-500">
        Scanner identities stay private. Manage collection and public count in your Passport subject settings.
      </p>
    </section>
  );
};

export default VehiclePassportOwnerPanel;
